import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getLiveWhatsAppChatMessages,
  getLiveWhatsAppChats,
  sendLiveWhatsAppChatMessage,
  streamLiveWhatsAppMessages,
} from '../config/api';
import './LiveWhatsApp.css';

const CHAT_PAGE_SIZE = 50;
const MESSAGE_PAGE_SIZE = 200;
const REFRESH_INTERVAL_MS = 60000;

const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const values = new Uint32Array(4);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => value.toString(16).padStart(8, '0')).join('');
};

const timestampValue = (value) => {
  const timestamp = new Date(value || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const participantLabel = (value) => {
  if (!value) return 'Unknown';
  const clean = String(value)
    .replace(/@(c|g)\.us$/i, '')
    .replace(/@s\.whatsapp\.net$/i, '');
  const digits = clean.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return clean;
};

const chatLabel = (chat) => chat?.chatName
  || (chat?.chatType === 'group' ? 'WhatsApp Group' : participantLabel(chat?.chatId));

const messageFallback = (message) => {
  const type = String(message?.messageType || 'message').replace(/_/g, ' ');
  return `[${type.charAt(0).toUpperCase()}${type.slice(1)}]`;
};

const formatMessageTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatListTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => {
    if (message?.id) byId.set(message.id, { ...byId.get(message.id), ...message });
  });
  return Array.from(byId.values()).sort(
    (a, b) => timestampValue(a.timestamp) - timestampValue(b.timestamp)
  );
};

const mergeChats = (current, incoming, replace = false) => {
  const byId = new Map((replace ? [] : current).map((chat) => [chat.chatId, chat]));
  incoming.forEach((chat) => {
    if (chat?.chatId) byId.set(chat.chatId, { ...byId.get(chat.chatId), ...chat });
  });
  return Array.from(byId.values()).sort(
    (a, b) => timestampValue(b.updatedAt) - timestampValue(a.updatedAt)
  );
};

const LiveWhatsApp = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [listError, setListError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [draftMessage, setDraftMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendFeedback, setSendFeedback] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const selectedChatRef = useRef(null);
  const chatRequestRef = useRef(0);
  const historyRequestRef = useRef(0);
  const historyOffsetRef = useRef(0);
  const messagePaneRef = useRef(null);
  const sendRefreshTimerRef = useRef(null);

  useEffect(() => () => window.clearTimeout(sendRefreshTimerRef.current), []);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadChats = useCallback(async ({ append = false, silent = false } = {}) => {
    const requestId = ++chatRequestRef.current;
    const offset = append ? chats.length : 0;
    if (append) setLoadingMoreChats(true);
    else if (!silent) setLoadingChats(true);

    try {
      const response = await getLiveWhatsAppChats({
        limit: CHAT_PAGE_SIZE,
        offset,
        search: debouncedSearch,
        type: filter,
      });
      if (requestId !== chatRequestRef.current) return;
      const page = response?.data?.chats || [];
      setChats((current) => mergeChats(current, page, !append));
      setHasMoreChats(Boolean(response?.data?.hasMore));
      setListError('');
    } catch (error) {
      if (requestId === chatRequestRef.current && !silent) {
        setListError(error.message || 'Unable to load WhatsApp chats');
      }
    } finally {
      if (requestId === chatRequestRef.current) {
        setLoadingChats(false);
        setLoadingMoreChats(false);
      }
    }
  }, [chats.length, debouncedSearch, filter]);

  const loadHistory = useCallback(async (chat, { older = false, silent = false, replace = false } = {}) => {
    if (!chat?.chatId) return;
    const requestId = older ? historyRequestRef.current : ++historyRequestRef.current;
    const offset = older ? historyOffsetRef.current : 0;
    const pane = messagePaneRef.current;
    const previousHeight = pane?.scrollHeight || 0;

    if (older) setLoadingOlder(true);
    else if (!silent) setLoadingMessages(true);

    try {
      const response = await getLiveWhatsAppChatMessages(chat.chatId, {
        limit: MESSAGE_PAGE_SIZE,
        offset,
      });
      if (requestId !== historyRequestRef.current || selectedChatRef.current?.chatId !== chat.chatId) return;
      const page = response?.data?.messages || [];
      setMessages((current) => mergeMessages((older || (silent && !replace)) ? current : [], page));
      if (older) historyOffsetRef.current += page.length;
      else if (!silent || replace) historyOffsetRef.current = page.length;
      if (!silent || replace) setHasOlderMessages(Boolean(response?.data?.hasMore));
      setHistoryError('');

      window.requestAnimationFrame(() => {
        const currentPane = messagePaneRef.current;
        if (!currentPane) return;
        if (older) currentPane.scrollTop += currentPane.scrollHeight - previousHeight;
        else currentPane.scrollTop = currentPane.scrollHeight;
      });
    } catch (error) {
      if (requestId === historyRequestRef.current && !silent) {
        setHistoryError(error.message || 'Unable to load this conversation');
      }
    } finally {
      if (requestId === historyRequestRef.current) {
        setLoadingMessages(false);
        setLoadingOlder(false);
      }
    }
  }, []);

  const selectChat = useCallback((chat) => {
    selectedChatRef.current = chat;
    historyOffsetRef.current = 0;
    setSelectedChat(chat);
    setMessages([]);
    setHasOlderMessages(false);
    setHistoryError('');
    setDraftMessage('');
    setSendFeedback(null);
    loadHistory(chat);
  }, [loadHistory]);

  useEffect(() => {
    loadChats();
  }, [debouncedSearch, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadChats({ silent: true });
      if (selectedChatRef.current) loadHistory(selectedChatRef.current, { silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadChats, loadHistory]);

  useEffect(() => {
    let stopped = false;
    let reconnectTimer;
    let controller;
    let attempt = 0;

    const connect = async () => {
      if (stopped) return;
      controller = new AbortController();
      setConnectionStatus(attempt === 0 ? 'connecting' : 'reconnecting');

      try {
        await streamLiveWhatsAppMessages({
          signal: controller.signal,
          onReady: () => {
            attempt = 0;
            setConnectionStatus('live');
          },
          onMessage: (message) => {
            setChats((current) => {
              const existing = current.find((chat) => chat.chatId === message.chatId);
              if (!existing) return current;
              return mergeChats(current, [{
                ...existing,
                chatName: message.chatName || existing.chatName,
                latestMessage: message,
                updatedAt: message.timestamp,
              }]);
            });

            if (selectedChatRef.current?.chatId === message.chatId) {
              setMessages((current) => mergeMessages(current, [message]));
              window.requestAnimationFrame(() => {
                const pane = messagePaneRef.current;
                if (pane) pane.scrollTop = pane.scrollHeight;
              });
            }
          },
        });
      } catch (error) {
        if (error.name === 'AbortError' || stopped) return;
      }

      if (!stopped) {
        attempt += 1;
        setConnectionStatus('reconnecting');
        reconnectTimer = window.setTimeout(connect, Math.min(1000 * (2 ** Math.min(attempt, 4)), 15000));
      }
    };

    connect();
    return () => {
      stopped = true;
      controller?.abort();
      window.clearTimeout(reconnectTimer);
    };
  }, []);

  const statusLabel = connectionStatus === 'live'
    ? 'Live'
    : connectionStatus === 'reconnecting' ? 'Reconnecting' : 'Connecting';

  const selectedTitle = useMemo(() => chatLabel(selectedChat), [selectedChat]);

  const refreshAll = () => {
    loadChats();
    if (selectedChat) loadHistory(selectedChat);
  };

  const sendMessage = async (event) => {
    event?.preventDefault();
    const chat = selectedChatRef.current;
    const message = draftMessage.trim();
    if (!chat?.chatId || !message || sendingMessage) return;

    const requestId = createRequestId();
    setSendingMessage(true);
    setSendFeedback(null);

    try {
      const response = await sendLiveWhatsAppChatMessage(chat.chatId, { message, requestId });
      const result = response?.data || {};
      const timestamp = result.queuedAt || new Date().toISOString();
      const optimisticMessage = {
        id: `queued:${result.uniqueId || requestId}`,
        chatId: chat.chatId,
        chatName: chat.chatName,
        chatType: chat.chatType,
        direction: 'outbound',
        sender: null,
        senderName: 'Zun team',
        body: message,
        messageType: 'chat',
        timestamp,
        media: null,
      };

      setChats((current) => mergeChats(current, [{
        ...chat,
        latestMessage: optimisticMessage,
        updatedAt: timestamp,
      }]));

      if (selectedChatRef.current?.chatId === chat.chatId) {
        setMessages((current) => mergeMessages(current, [optimisticMessage]));
        setDraftMessage('');
        setSendFeedback({ type: 'success', message: 'Message queued successfully.' });
        window.requestAnimationFrame(() => {
          const pane = messagePaneRef.current;
          if (pane) pane.scrollTop = pane.scrollHeight;
        });
      }

      window.clearTimeout(sendRefreshTimerRef.current);
      sendRefreshTimerRef.current = window.setTimeout(() => {
        if (selectedChatRef.current?.chatId === chat.chatId) {
          loadHistory(chat, { silent: true, replace: true });
        }
      }, 4000);
    } catch (error) {
      if (selectedChatRef.current?.chatId === chat.chatId) {
        setSendFeedback({
          type: 'error',
          message: error.message || 'Unable to send WhatsApp message',
        });
      }
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <main className="container live-wa-page">
      <section className="page-header live-wa-header">
        <div>
          <h1 className="page-title">Live WhatsApp</h1>
          <p className="page-subtitle">Live conversations from Periskope. Messages are not stored in OMS.</p>
        </div>
        <div className="live-wa-header-actions">
          <span className={`live-wa-status live-wa-status--${connectionStatus}`}>
            <span aria-hidden="true" /> {statusLabel}
          </span>
          <button type="button" className="btn btn-secondary" onClick={refreshAll}>Refresh</button>
        </div>
      </section>

      {listError && <div className="live-wa-alert" role="alert">{listError}</div>}

      <section className={`live-wa-inbox ${selectedChat ? 'live-wa-inbox--selected' : ''}`}>
        <aside className="live-wa-sidebar" aria-label="WhatsApp chats">
          <div className="live-wa-sidebar-heading">
            <strong>Inbox</strong>
            <span>{chats.length}</span>
          </div>
          <div className="live-wa-search-wrap">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search chats or numbers"
              aria-label="Search WhatsApp chats"
            />
          </div>
          <div className="live-wa-filters" role="group" aria-label="Chat type">
            {[
              ['all', 'All'],
              ['group', 'Groups'],
              ['individual', 'Individual'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={filter === value ? 'active' : ''}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="live-wa-chat-list">
            {loadingChats ? (
              <div className="live-wa-empty">Loading chats from Periskope…</div>
            ) : chats.length === 0 ? (
              <div className="live-wa-empty">No chats match this filter.</div>
            ) : (
              chats.map((chat) => {
                const latest = chat.latestMessage;
                return (
                  <button
                    type="button"
                    key={chat.chatId}
                    className={`live-wa-chat-row ${selectedChat?.chatId === chat.chatId ? 'active' : ''}`}
                    onClick={() => selectChat(chat)}
                  >
                    <span className={`live-wa-avatar live-wa-avatar--${chat.chatType}`} aria-hidden="true">
                      {chat.chatType === 'group' ? 'G' : 'P'}
                    </span>
                    <span className="live-wa-chat-summary">
                      <span className="live-wa-chat-name">{chatLabel(chat)}</span>
                      <span className="live-wa-chat-preview">
                        {latest ? (latest.body || messageFallback(latest)) : participantLabel(chat.chatId)}
                      </span>
                    </span>
                    <span className="live-wa-chat-meta">
                      <time>{formatListTime(chat.updatedAt)}</time>
                      {chat.unreadCount > 0 && <span>{Math.min(chat.unreadCount, 99)}</span>}
                    </span>
                  </button>
                );
              })
            )}
            {hasMoreChats && (
              <button
                type="button"
                className="live-wa-load-more"
                disabled={loadingMoreChats}
                onClick={() => loadChats({ append: true })}
              >
                {loadingMoreChats ? 'Loading…' : 'Load more chats'}
              </button>
            )}
          </div>
        </aside>

        <section className="live-wa-conversation" aria-live="polite">
          {!selectedChat ? (
            <div className="live-wa-no-selection">
              <span aria-hidden="true">◌</span>
              <strong>Select a chat to view</strong>
              <p>Choose a group or individual conversation from the inbox.</p>
            </div>
          ) : (
            <>
              <header className="live-wa-conversation-header">
                <button
                  type="button"
                  className="live-wa-back"
                  aria-label="Back to chats"
                  onClick={() => setSelectedChat(null)}
                >
                  ←
                </button>
                <span className={`live-wa-avatar live-wa-avatar--${selectedChat.chatType}`} aria-hidden="true">
                  {selectedChat.chatType === 'group' ? 'G' : 'P'}
                </span>
                <div>
                  <strong>{selectedTitle}</strong>
                  <span>
                    {selectedChat.chatType === 'group'
                      ? `${selectedChat.memberCount || 'WhatsApp'} members`
                      : participantLabel(selectedChat.chatId)}
                  </span>
                </div>
              </header>

              {historyError && <div className="live-wa-history-error" role="alert">{historyError}</div>}

              <div className="live-wa-message-pane" ref={messagePaneRef}>
                {hasOlderMessages && (
                  <button
                    type="button"
                    className="live-wa-load-older"
                    disabled={loadingOlder}
                    onClick={() => loadHistory(selectedChat, { older: true })}
                  >
                    {loadingOlder ? 'Loading…' : 'Load older messages'}
                  </button>
                )}
                {loadingMessages ? (
                  <div className="live-wa-empty">Loading conversation…</div>
                ) : messages.length === 0 ? (
                  <div className="live-wa-empty">No messages are available in this chat.</div>
                ) : (
                  messages.map((message) => {
                    const attachmentUrl = /^https:\/\//i.test(message.media?.url || '')
                      ? message.media.url
                      : null;
                    return (
                      <article
                        className={`live-wa-bubble-row live-wa-bubble-row--${message.direction}`}
                        key={message.id}
                      >
                        <div className="live-wa-bubble">
                          {selectedChat.chatType === 'group' && message.direction === 'inbound' && (
                            <strong>{message.senderName || participantLabel(message.sender)}</strong>
                          )}
                          <p>{message.body || messageFallback(message)}</p>
                          {message.media && (
                            <div className="live-wa-attachment">
                              <span>{message.media.filename || message.media.mimetype || 'Attachment'}</span>
                              {attachmentUrl && (
                                <a href={attachmentUrl} target="_blank" rel="noreferrer">View</a>
                              )}
                            </div>
                          )}
                          <time dateTime={message.timestamp}>{formatMessageTime(message.timestamp)}</time>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
              <form className="live-wa-composer" onSubmit={sendMessage}>
                <textarea
                  value={draftMessage}
                  onChange={(event) => {
                    setDraftMessage(event.target.value);
                    if (sendFeedback) setSendFeedback(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      sendMessage(event);
                    }
                  }}
                  maxLength={4096}
                  rows={1}
                  disabled={sendingMessage}
                  placeholder="Type a message"
                  aria-label={`Message ${selectedTitle}`}
                />
                <button
                  type="submit"
                  disabled={sendingMessage || !draftMessage.trim()}
                >
                  {sendingMessage ? 'Sending…' : 'Send'}
                </button>
                <div className="live-wa-composer-meta">
                  <span>Enter to send · Shift + Enter for a new line</span>
                  <span>{draftMessage.length}/4096</span>
                </div>
                {sendFeedback && (
                  <div className={`live-wa-send-feedback live-wa-send-feedback--${sendFeedback.type}`} role="status">
                    {sendFeedback.message}
                  </div>
                )}
              </form>
            </>
          )}
        </section>
      </section>
    </main>
  );
};

export default LiveWhatsApp;
